/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {MutableRefObject, useCallback} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * A custom hook that returns a memoized callback function to invoke a specific method
 * on an object referenced by the provided `ref`. This hook is particularly useful for
 * triggering methods such as `onRefresh`, `toggleColumnChooser`, or any other method
 * that exists within the referenced object.
 * 
 * ```typescript
 * 
 * import {useRefCallback} from '@ion/applications/src/hooks/useRefCallback';
 * 
 * const onRefresh = useRefCallback(gridRef, 'onRefresh');
 * ```
 * 
 * @param ref - A mutable reference to an object that contains the method to be invoked.
 * @param method - The name of the method within the referenced object to be called.
 * 
 * @returns A memoized callback function that, when called, triggers the specified method
 *          of the object referenced by `ref`.
 */
export const useRefCallback = (ref: MutableRefObject<GenericObject>, method: string) => (
  useCallback(async(params?: any) => {
    await ref.current?.[method]?.(params);
  }, [ref])
);

export default useRefCallback;
